import { useState } from 'react';
import { Button, Hidden, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Upload } from 'ts-react-feather-icons';

import Modal from 'common/modal';
import { UPLOAD_LIMIT_BYTES, UPLOAD_LIMIT_STRING } from 'utils/constants';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  button: {
    color: 'white',
    marginRight: '10px',
    marginBottom: '10px',
  },
  buttonIcon: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px',
    justifyContent: 'center',
  },
}));

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  uploadCompletedFn?: Function;
  uploadType?: string;
};

type SuccessProps = {
  fileName: string;
};

const UploadSuccess = ({ fileName }: SuccessProps) => {
  return (
    <div style={{ paddingBottom: '50px' }}>
      <img src="/checkmark-icon.svg" alt="Success!" />
      <Typography>
        You successfully uploaded
        <span>
          <b> {fileName} </b>
        </span>
      </Typography>
    </div>
  );
};

const PublicDocumentUploadButton = ({ uploadType, uploadCompletedFn }: Props) => {
  const classes = useStyles();
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = ({ target: { files } }: any) => {
    if (!files.length) {
      return;
    }

    // calculate upload size
    let uploadSizeInBytes = 0;
    for (const file of files) {
      uploadSizeInBytes += file.size;
    }
    if (uploadSizeInBytes > UPLOAD_LIMIT_BYTES) {
      showAlert({
        message: `Error: File Size Too Large - Please reduce the size of this file to less than ${UPLOAD_LIMIT_STRING} and retry.`,
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    setUploadedFileName(files[0]?.name);
    setShowSuccessModal(true);

    if (uploadCompletedFn) {
      uploadCompletedFn(files[0]);
    }
  };

  // namespace the upload forms if a type is specified
  const id = 'file_input' + uploadType;

  return (
    <>
      <input data-cy={id} id={id} type="file" onChange={handleChange} hidden />
      <label htmlFor={id}>
        <Button
          component="span"
          className={classes.button}
          color="primary"
          variant="contained"
          size="small"
        >
          <div className={classes.buttonIcon}>
            <Upload size={20} />
          </div>
          <Hidden mdDown>
            <Typography>Upload</Typography>
          </Hidden>
        </Button>
      </label>
      <Modal
        onActionClick={() => {
          return;
        }}
        setShowModal={setShowSuccessModal}
        showModal={showSuccessModal}
        onExitClick={() => {
          setShowSuccessModal(false);
        }}
        content={<UploadSuccess fileName={uploadedFileName} />}
        title={'Success!'}
        actionVerb="Delete"
        showActions={false}
      />
    </>
  );
};

export default PublicDocumentUploadButton;
